import { Add } from "@mui/icons-material";
import {
  Avatar,
  Box,
  CircularProgress,
  Stack,
  SxProps,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import { handleChoiceModification } from "../../../../Redux/reducers/questionTranslationSlice";
import { useCallback, useState } from "react";
import { errorToastMessage, toastMessage } from "../../../../utils/toast";
import { builderGetUploadUrl } from "../../../../utils/upload";
import { useDropzone } from "react-dropzone";
import { useParams } from "react-router-dom";

type Props = {
  question: any;
};

type UploadProps = {
  choiceId: string;
  previewUrl: string;
};

const previewWrapper: SxProps = {
  width: "100%",
  height: 50,
  borderRadius: "8px",
  border: 2,
  borderColor: "primary.main",
  borderStyle: "dashed",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "4px",
  "&:hover": {
    bgcolor: "secondary.dark",
    cursor: "pointer",
  },
};

const uploadWrapper: SxProps = {
  width: "100%",
  height: 50,
  borderRadius: "8px",
  border: 2,
  borderColor: "primary.main",
  borderStyle: "dashed",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  "&:hover": {
    bgcolor: "secondary.dark",
    cursor: "pointer",
  },
};

const UploadItem = ({ choiceId, previewUrl }: UploadProps) => {
  const { id: studyId } = useParams();
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const isPreview = useAppSelector(
    ({ questionTranslation }) => !questionTranslation.editable
  );
  const formId = useAppSelector((state) => state.questionTranslation.formId);
  const formLevel = useAppSelector(
    (state) => state.questionTranslation.formLevel
  );

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      try {
        const file = acceptedFiles?.[0];
        if (file && studyId) {
          if (file.size > 5 * 1024 * 1024) {
            toastMessage("warning", "File Size cannot be greater than 5 MB!");
            return;
          }

          setLoading(true);

          const obj = await builderGetUploadUrl(
            file,
            studyId,
            formId,
            formLevel
          );

          if (obj.postUploadImageUrl) {
            dispatch(
              handleChoiceModification({
                currentRef: choiceId,
                section: "imageUrl",
                value: obj.postUploadImageUrl,
              })
            );
          }
          if (obj.previewUrl) {
            dispatch(
              handleChoiceModification({
                currentRef: choiceId,
                section: "previewUrl",
                value: obj.previewUrl,
              })
            );
          }
          setLoading(false);
        }
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    },
    [dispatch, formId, studyId, choiceId, formLevel]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "image/jpg": [],
      "image/jpeg": [],
      "image/png": [],
      // "application/pdf": [],
    },
    disabled: isPreview,
  });

  return (
    <Stack gap={1}>
      {loading ? (
        <Box sx={{ color: "text.secondary" }}>
          <CircularProgress />
        </Box>
      ) : previewUrl ? (
        <Tooltip title={"Upload Image"} placement="right">
          <Box {...getRootProps({ className: "dropzone" })} sx={previewWrapper}>
            <input {...getInputProps()} />
            <Avatar
              src={previewUrl}
              variant="rounded"
              sx={{ width: "100%", height: 40 }}
            />
          </Box>
        </Tooltip>
      ) : (
        <Stack
          direction={"row"}
          alignItems={"center"}
          gap={1}
          {...getRootProps({ className: "dropzone" })}
          sx={{ ...uploadWrapper }}
        >
          <input {...getInputProps()} />

          <Stack alignItems={"center"} gap={1}>
            <Add fontSize="medium" sx={{ color: "text.secondary" }} />
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

const ChoiceTab = ({ question }: Props) => {
  const dispatch = useAppDispatch();

  const { choices }: { choices: any[] } = question || [];
  const isChoicesError = useAppSelector(
    ({ questionTranslation }) => questionTranslation.isChoicesError
  );
  const isPreview = useAppSelector(
    ({ questionTranslation }) => !questionTranslation.editable
  );

  return (
    <Stack gap={1} py={1}>
      {isChoicesError && (
        <Typography variant="caption" fontWeight="medium" color={"red"}>
          {isChoicesError}
        </Typography>
      )}

      <Stack direction="row" alignItems={"center"} mb={3} gap={2}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            minWidth: "1px",
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: "medium" }}>
            Label
          </Typography>
        </Box>

        {question.type !== "dropdown" && (
          <Typography
            variant="body1"
            sx={{ fontWeight: "medium", width: "100px" }}
          >
            Image
          </Typography>
        )}
      </Stack>

      {choices &&
        choices.map((choice, i) => (
          <Stack
            key={choice.questionChoiceId}
            direction="row"
            alignItems={"center"}
            mb={3}
            gap={2}
          >
            <Box sx={{ flex: 1, minWidth: "1px" }}>
              <Typography
                fontWeight={"regular"}
                variant="body1"
                mb={1}
                color="text.primary"
              >
                {choice.header}
              </Typography>
              <TextField
                fullWidth
                placeholder="Label"
                value={choice.label}
                onChange={(e) =>
                  dispatch(
                    handleChoiceModification({
                      currentRef: choice.questionChoiceId,
                      section: "label",
                      value: e.target.value,
                    })
                  )
                }
                name="option-label"
                InputProps={{
                  readOnly: isPreview,
                }}
              />
            </Box>

            {question.type !== "dropdown" && (
              <Box sx={{ width: "100px", alignSelf: "flex-end" }}>
                <UploadItem
                  choiceId={choice.questionChoiceId}
                  previewUrl={choice.previewUrl}
                />
              </Box>
            )}
          </Stack>
        ))}
    </Stack>
  );
};
export default ChoiceTab;
